/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';

import { Link } from 'react-router-dom';

import { StatusIcon } from '../../../../components/SVGIcon';
import styles from '../../../../styles';
import Localized from '../../../../../shared/components/Localized';

const Wrapper = styled.div`
  border: 1px solid ${styles.colors.geyser};
  width: 20rem;

  &:first-child {
    border-radius: .25rem 0 0 .25rem;
  }

  &:nth-child(4) {
    border-radius: 0 .25rem .25rem 0;
  }

  &:not(:first-child) {
    border-left: 0px solid ${styles.colors.geyser};
  }
`;

const Header = styled.div`
  ${styles.fonts.headingLabelCaps}
  background-color: ${styles.colors.linkWater};
  border-bottom: 1px solid ${styles.colors.geyser};

  padding: 0.5rem 0 0.5rem 1rem;
`;
const Body = styled.div`
  padding: 0.5rem 0 0.5rem 1rem;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
`;

const ItemLink = styled(Link)`
  ${styles.fonts.linkResults}
  padding-left: 0.5rem;
`;

const Service = ({ service, instance, missingTranslations }) => {
  const serviceKey = service.key;

  const ongoingIncidents = instance.Incidents.reduce((acc, incident) => {
    if (incident.IncidentImpacts[0] &&
      !incident.IncidentImpacts[0].endTime &&
      incident.serviceKeys.includes(serviceKey)) acc.push(incident);
    return acc;
  }, []);

  const ongoingMaintenances = instance.Maintenances.reduce((acc, maintenance) => {
    if (maintenance.serviceKeys.includes(serviceKey)) acc.push(maintenance);
    return acc;
  }, []);

  return (
    <Wrapper>
      <Header>
        <Localized
          modelName='service'
          modelKey={service.key}
          modelAttribute='label'
          missingTranslations={missingTranslations}
        />
      </Header>
      <Body>
        {ongoingIncidents.map((incident, i) => (
          <Item key={i}>
            <StatusIcon size='1rem' severity={incident.IncidentImpacts[0].endTime ? 'healthy' : incident.IncidentImpacts[0].severity} />
            <ItemLink
              to={{
                pathname: `/incidents/${incident.id}`,
                state: { modal: true }
              }}
            >
              {incident.id}
            </ItemLink>
          </Item>
        ))}
        {ongoingMaintenances.map((maintenance, i) => (
          <Item key={i}>
            <StatusIcon size='1rem' severity='maintenance' />
            <ItemLink
              to={{
                pathname: `/maintenances/${maintenance.id}`,
                state: { modal: true }
              }}
            >
              {maintenance.id}
            </ItemLink>
          </Item>
        ))}
        {ongoingIncidents.length === 0 && ongoingMaintenances.length === 0 && (
          <Item>
            <StatusIcon size='1rem' severity='healthy' />
          </Item>
        )}
      </Body>
    </Wrapper>
  );
};

Service.propTypes = {
  sevice: PropTypes.shape({
    key: PropTypes.string,
  }),
  instance: PropTypes.shape({
    key: PropTypes.string,
    serviceKeys: PropTypes.arrayOf(PropTypes.string),
    Incidents: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      IncidentImpacts: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        startTime: PropTypes.object,
        endTime: PropTypes.object
      })),
    })),
    Maintenances: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      serviceKeys: PropTypes.arrayOf(PropTypes.string),
      MaintenanceImpacts: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        startTime: PropTypes.object,
        endTime: PropTypes.object
      })),
    }))
  })
};

export default Service;
